export type BaseEntity = {
    id:number,
    createdAt: string,
}

export type Entity<T> = {
    [K in keyof T]: T[K];
} & BaseEntity;

export type User = Entity<{
    email: string,
    role: string,
    firstName?: string,
    lastName?: string,
    department?: string,
    verified: boolean,
    isImpersonated: boolean,
    passwordResetRequired: boolean,
    emailNotificationMethods: EmailNotificationMethod[],
    smsNotificationMethods: SmsNotificationMethod[],
    apnSubscriptions: GuardApnSubscription[],
    nameSubscriptions: GuardNameSubscription[],
    administeringCounties: AdministeringCounty[]
}>;

export type CountyConfiguration = {
    id: number;
    name: string;
    code: string;
    uiMask: string;
    dataMask: string;
    format: string;
    linkUrl: string;
    alertSendGridEmailTemplateId: string;
    createCountyMailer: boolean;
    mailerDocumentTypes: Array<string>;
    mailerTemplatePath: string;
    useOnePageMailer: boolean;
    mailerOutputPath: string;
    useWatermark: boolean;
    createdAt: string;
    updatedAt: string;
    isEnabled: boolean;
    addSampleToMailer: boolean;
    grayscaleMailer: boolean;
    countyAdministrators: CountyAdministrator[];
    countyMailerCreationLogs: CountyMailerCreationLog[];
    emailConfiguration?: {
        countyId: number;
        emailAddress: string;
        id: string;
    },
    ftpConfiguration?: {
        countyId: number;
        host: string;
        port: number;
        username: string;
        remotePath: string;
        id: string;
        password: string;
        useSSL: boolean;
        usePassive: boolean;
        protocol: "ftp" | "sftp";
    },
    deliveryMethod?: "email" | "ftp" | null;

}

export type ReadOnlyDashboardConfiguration = {
    id: number;
    name: string;
    code: string;
    hasLinkUrl: boolean;
    linkUrl: string;
    hasAlertSendGridEmailTemplateId: boolean;
    alertSendGridEmailTemplateId: string;
    hasMailerDocumentTypes: boolean;
    mailerDocumentTypes: string[];
    useOnePageMailer: boolean;
    useWatermark: boolean;
    createdAt: string;
    updatedAt: string;
    isGuardDogEnabled: boolean;
    addSampleToMailer: boolean;
    grayscaleMailer: boolean;
    hasMailerTemplatePath: boolean;
    mailerTemplatePath: string;
    createCountyMailer: boolean;
    isDeliveryEnabled: boolean;
    lastFileReceived: string;
    lastFileProcessed: string;
    lastFileCreatedForCountyMailer: string;
    lastDocumentReceived: string;
}

export type CountyMailerCreationLog = Entity<{
    countyCode: string;
    totalRecords: number;
    totalRecordsMatchingDocumentTypes: number;
    includedDocumentTypes: string[];
    lastPrinted?: string;
    totalRecordsSuccessfullyCreated: number;
    totalRecordsFailed: number;
    minDate: string;
    maxDate: string;
    minDocumentNumber: string;
    maxDocumentNumber: string;
    batchId: string;
    grantorGranteeIndexRecords: CountyMailerGrantorGranteeIndexRecord[];
    grantorGranteeIndexFailedRecords : CountyMailerGrantorGranteeIndexFailedRecord[];
    regenerateMailersInProgress?: boolean;
    jobId?: string;
}>;

export type CountyMailerGrantorGranteeIndexRecord = Entity<{
    countyCode: string;
    documentNumber: string;
    documentTitle: string;
    documentDate: string;
    lastPrinted?: string;
    apn: string;
    isMailed: boolean;
    mailedAt?: string;
    generatingInProcess?: boolean;
    jobId?: string;

}>;

export type CountyMailerGrantorGranteeIndexFailedRecord = Entity<{
    countyCode: string;
    documentNumber: string;
    documentTitle: string;
    documentDate: string;
    apn: string;
    names?: string;
    situsAddressOrLegalDescription?: string;
    mailingAddress?: string;
    mailingCity?: string;
    mailingState?: string;
    mailingZip?: string;
    failureReason: string;
    recreated: boolean;
    recreatedAt?: string;
    mailedAt?: string;
    isMailed: boolean;
    generatingInProcess?: boolean;
    jobId?: string;
}>;

export type CountyAdministrator = Entity<{
    userId: number;
    countyCode: string;
    county: string;
    countyId: number;
    email: string;
    department: string;
    firstName: string;
    lastName: string;
    isActive: boolean;
    updatedAt: string;
}>

export const guardRecordSubscriptionType = {
    apn: 'Property',
    name: 'Name',
} as const;

export type GuardRecordSubscriptionType = (typeof guardRecordSubscriptionType)[keyof typeof guardRecordSubscriptionType];

export const notificationType = {
    email: 'Email',
    sms: 'SMS',
} as const;

export type NotificationType =(typeof notificationType)[keyof typeof notificationType];

export type SmsNotificationMethod = Entity<{
    phoneNumber: string,
    verified: boolean,
    verifiedAt: string,
}>

export type EmailNotificationMethod = Entity<{
    email: string,
    verified: boolean,
    verifiedAt: string,
}>;
type BaseGuardSubscription = Entity<{
    county: string,
    events: GuardRecordFoundEvent[],
}>;

export type GuardApnSubscription = {
    apn: string,
    description?: string
} & BaseGuardSubscription;


export type GuardNameSubscription = {
    name: string,
} & BaseGuardSubscription;

export type AdministeringCounty = {
    countyCode: string,
    countyName: string,
}

export type GuardRecordFoundEvent  = Entity<{
    parsedDocumentName: string,
    documentNumber: string,
    documentDate: string,
    countyCodeContainerName: string,
    notifications: GuardedRecordFoundNotification[],
    county: string,
    matchedValue: string,
    isArchived: boolean,
    archivedAt?: string,
}>

export type GuardedRecordFoundNotification = Entity<{
    notificationMethodId: string,
}>

export type AddGuardAPNSubscriptionRequest = {
    county: string,
    description?: string,
    apn: string,
}


export type AddGuardNameSubscriptionRequest = {
    county: string,
    name: string,
}
export type RemoveGuardSubscriptionRequest = {
    guardSubscriptionId: string,
}

export type AddNotificationMethodRequest = {
    type: NotificationType,
    email?: string,
    phoneNumber?: string,
}

export type RemoveNotificationMethodRequest = {
    notificationMethodId: string,
}

export type ResendNotificationVerificationRequest = {}  & RemoveNotificationMethodRequest


export type RegisterRequest = {
    email: string
    apns: AddGuardAPNSubscriptionRequest[],
    names: AddGuardNameSubscriptionRequest[],
}

export type LoginRequest = {
    email: string
}
export type AdminLoginRequest = {
    email: string,
    password: string
}

export type LoginRedirectRequest = {
    code: string
}

export type VerifyEmailRequest = {
    code: string
}

export type VerifySmsRequest = {
    code: string
}

export type ResetPasswordRequest = {
    userId: number;
    token: string;
    email: string;
    newPassword: string;
    confirmPassword: string;
}

export type ApnAddressSearchResult = {
    apn: string,
    address: string,
    county: string,
    cityStateZip: string,
    loading: boolean,
}

export type NameSearchResult = {
    name: string,
    county: string,
    loading: boolean,
    custom: boolean,
}

export type AssessorAddressResult = {
    apn: string,
    names: string,
    situsAddressOrLegalDescription: string,
    mailingAddress: string,
    mailingCity: string,
    mailingState: string,
    mailingZip: string,
}

export type AuthContextType = {
    currentUser: User | null,
    loading: boolean,
    login: (loginRequest: LoginRequest) => void,
    adminLogin: (loginRequest: AdminLoginRequest) => void,
    impersonate: (userId: number) => void,
    loginRedirect: (loginRedirectRequest: LoginRedirectRequest) => void,
    verifyEmail: (verifyEmailRequest: VerifyEmailRequest) => void,
    verifySMS: (verifySmsRequest: VerifySmsRequest) => void,
    logout: () => void,
    resetPassword: (resetPasswordRequest: ResetPasswordRequest) => Promise<boolean>,
    setAuthenticatedUser: (user: User) => void,
}

export type MetricsResult = {
  totalUsers: number;
  totalApnSubscriptions: number;
  totalNameSubscriptions: number;
  subscriptionsApnByCounty: Record<string, number>;
  subscriptionsNameByCounty: Record<string, number>;
  activeUsersInLast30Days: number;
  activeUsersInLast30DaysByCounty: Record<string, number>;
  totalRegisteredInLast30Days: number;
  totalRegisteredInLast30DaysByCounty: Record<string, number>;
  totalRegisteredInLast7Days: number;
  totalRegisteredInLast7DaysByCounty: Record<string, number>;
  totalApnEvents: number;
  totalNameEvents: number;
  apnEventsByCounty: Record<string, number>;
  nameEventsByCounty: Record<string, number>;
  usersByCounty: Record<string, number>;
  counties: string[];
};

// Payment configuration for Accept.js
export type PaymentConfig = {
  apiLoginId: string;
  publicClientKey: string;
  environment: 'sandbox' | 'production';
};

// Cart types
export * from './cart';

// Checkout types
export * from './checkout';

// Document Access types
export * from './documentAccess';

